import { INITIAL } from "./components/organisms/FilterDrawerPanels/filterOptions";

const DEFAULT_SORT_ORDER = "relevant";
const SORT_ORDERS = new Set([
  DEFAULT_SORT_ORDER,
  "last_active",
  "new_skill_listing",
]);

const ARRAY_FILTER_KEYS = [
  "jobTitleInclude",
  "jobTitleExclude",
  "jobFunction",
  "seniority",
  "companyInclude",
  "companyExclude",
  "industry",
  "companySize",
  "location",
  "cityId",
  "cityName",
  "regionId",
  "regionName",
  "countryId",
  "countryName",
  "visaStatusId",
  "experienceLevel",
  "currentWorkLocation",
  "pastWorkLocation",
  "remoteLocation",
  "skill",
  "yearsOfExp",
  "yearsOfExpCurrentRole",
  "school",
  "language",
];

const STRING_FILTER_KEYS = [
  "name",
  "keyword",
  "domain",
  "skillListingType",
  "budgetMin",
  "budgetMax",
  "budgetType",
];

const BOOLEAN_FILTER_KEYS = [
  "currentTitlesOnly",
  "includeRelatedTitles",
  "companyCurrentOnly",
  "companyIncludeRelated",
];

const NUMBER_FILTER_KEYS = ["locationRadius", "ratingMin", "minRate", "maxRate"];

const cloneInitialFilters = () => ({
  ...INITIAL,
  jobTitleInclude: [],
  jobTitleExclude: [],
  jobFunction: [],
  seniority: [],
  companyInclude: [],
  companyExclude: [],
  industry: [],
  companySize: [],
  location: [],
  currentWorkLocation: [],
  pastWorkLocation: [],
  remoteLocation: [],
  skill: [],
  yearsOfExp: [],
  yearsOfExpCurrentRole: [],
  school: [],
  language: [],
});

const toArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const isActiveFilterValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "boolean") return value;
  return value !== null && value !== undefined && value !== "";
};

export const hasActiveQueryFilters = (filters = INITIAL) =>
  [
    ...ARRAY_FILTER_KEYS,
    ...STRING_FILTER_KEYS,
    ...BOOLEAN_FILTER_KEYS,
    ...NUMBER_FILTER_KEYS,
  ].some((key) => isActiveFilterValue(filters[key]));

export const parseQueryResultState = (search = "") => {
  const params = new URLSearchParams(search);
  const filters = cloneInitialFilters();

  STRING_FILTER_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) filters[key] = value;
  });

  ARRAY_FILTER_KEYS.forEach((key) => {
    filters[key] = params.getAll(key).filter(Boolean);
  });

  BOOLEAN_FILTER_KEYS.forEach((key) => {
    filters[key] = params.get(key) === "1";
  });

  NUMBER_FILTER_KEYS.forEach((key) => {
    const rawValue = params.get(key);
    if (rawValue !== null && rawValue !== "") {
      const value = Number(rawValue);
      if (Number.isFinite(value)) filters[key] = value;
    }
  });

  const rawPage = Number(params.get("page"));
  const currentPage = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const rawSort = params.get("sort");
  const sortOrder = SORT_ORDERS.has(rawSort) ? rawSort : DEFAULT_SORT_ORDER;

  return {
    filters,
    sortOrder,
    currentPage,
    hasAppliedFilters: hasActiveQueryFilters(filters),
  };
};

export const buildQueryResultSearch = ({
  filters = INITIAL,
  sortOrder = DEFAULT_SORT_ORDER,
  currentPage = 1,
} = {}) => {
  const params = new URLSearchParams();

  STRING_FILTER_KEYS.forEach((key) => {
    const value = filters?.[key];
    if (value) params.set(key, value);
  });

  ARRAY_FILTER_KEYS.forEach((key) => {
    toArray(filters?.[key]).forEach((value) => params.append(key, value));
  });

  BOOLEAN_FILTER_KEYS.forEach((key) => {
    if (filters?.[key]) params.set(key, "1");
  });

  NUMBER_FILTER_KEYS.forEach((key) => {
    const value = filters?.[key];
    if (value !== null && value !== undefined && value !== "") {
      params.set(key, String(value));
    }
  });

  if (sortOrder && sortOrder !== DEFAULT_SORT_ORDER) {
    params.set("sort", sortOrder);
  }

  if (Number(currentPage) > 1) {
    params.set("page", String(Number(currentPage)));
  }

  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : "";
};
